import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  IncidentCategory,
  IncidentPartyType,
  IncidentSeverity,
  ReportStatus,
} from '@dsr/shared-types';
import {
  useIncident,
  useUpdateIncident,
  useTransitionIncident,
  useDeleteIncident,
  useIncidentEvents,
  useCreateIncidentEvent,
  useIncidentParties,
  useCreateIncidentParty,
  useDeleteIncidentParty,
} from '../api/queries/incidents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Textarea,
} from '../components/ui';
import { fmtDateTime } from '../utils/date';
import {
  CATEGORY_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  SeverityBadge,
  StatusBadge,
  firstFieldError,
} from './IncidentsPage';
import './reports.css';

const CATEGORIES = Object.keys(CATEGORY_LABEL) as IncidentCategory[];
const SEVERITIES: IncidentSeverity[] = ['info', 'low', 'medium', 'high', 'critical'];

const PARTY_TYPE_LABEL: Record<IncidentPartyType, string> = {
  subject: 'Subject',
  witness: 'Witness',
  victim: 'Victim',
  officer: 'Officer',
  vehicle: 'Vehicle',
};
const PARTY_TYPES = Object.keys(PARTY_TYPE_LABEL) as IncidentPartyType[];

// Allowed forward/back transitions a reviewer can pick from, keyed by current
// status. Mirrors the lifecycle in shared-types ReportStatus; the API enforces
// the same gating via incident.review.
const NEXT_STATUSES: Record<ReportStatus, ReportStatus[]> = {
  draft: ['submitted'],
  submitted: ['under_review', 'approved', 'rejected'],
  under_review: ['approved', 'rejected'],
  approved: ['under_review'],
  rejected: ['under_review'],
};

export function IncidentDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();

  const { data, isPending, error } = useIncident(Number.isInteger(id) && id > 0 ? id : null);
  const eventsQuery = useIncidentEvents(Number.isInteger(id) && id > 0 ? id : null);
  const partiesQuery = useIncidentParties(Number.isInteger(id) && id > 0 ? id : null);

  const updateIncident = useUpdateIncident();
  const transitionIncident = useTransitionIncident();
  const deleteIncident = useDeleteIncident();
  const createEvent = useCreateIncidentEvent();
  const createParty = useCreateIncidentParty();
  const deleteParty = useDeleteIncidentParty();

  // ---- Editor state (hydrated from the loaded incident) ----
  const [category, setCategory] = useState<IncidentCategory>('other');
  const [severity, setSeverity] = useState<IncidentSeverity>('low');
  const [occurredAt, setOccurredAt] = useState<number | null>(null);
  const [narrative, setNarrative] = useState('');
  const [reportNumber, setReportNumber] = useState('');
  const [policeNotified, setPoliceNotified] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const incident = data?.incident;
  useEffect(() => {
    if (!incident) return;
    setCategory(incident.category);
    setSeverity(incident.severity);
    setOccurredAt(incident.occurred_at);
    setNarrative(incident.narrative ?? '');
    setReportNumber(incident.report_number ?? '');
    setPoliceNotified(incident.police_notified);
  }, [incident]);

  // ---- Sub-form state ----
  const [showEvent, setShowEvent] = useState(false);
  const [eventType, setEventType] = useState('');
  const [eventDesc, setEventDesc] = useState('');
  const [eventAt, setEventAt] = useState<number | null>(Date.now());
  const [eventError, setEventError] = useState<string | null>(null);

  const [showParty, setShowParty] = useState(false);
  const [partyType, setPartyType] = useState<IncidentPartyType>('witness');
  const [partyName, setPartyName] = useState('');
  const [partyContact, setPartyContact] = useState('');
  const [partyDetails, setPartyDetails] = useState('');
  const [partyError, setPartyError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isPending) return <p className="rpt-status-line">Loading incident…</p>;
  if (error || !incident) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view this incident."
        : error instanceof ApiError && error.status === 404
          ? 'Incident not found.'
          : 'Failed to load incident.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const canWrite = can('incident.write');
  const canReview = can('incident.review');
  const events = eventsQuery.data?.events ?? [];
  const parties = partiesQuery.data?.parties ?? [];

  const onSaveEdit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEditError(null);
    try {
      await updateIncident.mutateAsync({
        id,
        category,
        severity,
        occurred_at: occurredAt,
        narrative: narrative.trim() || null,
        report_number: reportNumber.trim() || null,
        police_notified: policeNotified,
      });
      toast.success('Incident updated.');
    } catch (err) {
      setEditError(firstFieldError(err, 'Could not save changes.'));
    }
  };

  const onTransition = async (status: ReportStatus): Promise<void> => {
    const note =
      status === 'rejected'
        ? window.prompt('Reason for rejection (optional):') ?? undefined
        : undefined;
    try {
      await transitionIncident.mutateAsync({ id, status, ...(note ? { note } : {}) });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not change status.'));
    }
  };

  const onAddEvent = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEventError(null);
    if (!eventType.trim()) {
      setEventError('Event type is required.');
      return;
    }
    try {
      await createEvent.mutateAsync({
        incidentId: id,
        event_type: eventType.trim(),
        event_at: eventAt ?? undefined,
        description: eventDesc.trim() || undefined,
      });
      toast.success('Timeline updated.');
      setShowEvent(false);
      setEventType('');
      setEventDesc('');
      setEventAt(Date.now());
    } catch (err) {
      setEventError(firstFieldError(err, 'Could not add the event.'));
    }
  };

  const onAddParty = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setPartyError(null);
    try {
      await createParty.mutateAsync({
        incidentId: id,
        party_type: partyType,
        name: partyName.trim() || undefined,
        contact: partyContact.trim() || undefined,
        details: partyDetails.trim() || undefined,
      });
      toast.success('Party added.');
      setShowParty(false);
      setPartyName('');
      setPartyContact('');
      setPartyDetails('');
    } catch (err) {
      setPartyError(firstFieldError(err, 'Could not add the party.'));
    }
  };

  const onDelete = async (): Promise<void> => {
    try {
      await deleteIncident.mutateAsync(id);
      toast.success('Incident deleted.');
      navigate('/incidents');
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not delete the incident.'));
      setConfirmDelete(false);
    }
  };

  return (
    <section className="rpt-page">
      <button type="button" className="rpt-back" onClick={() => navigate('/incidents')}>
        ← Back to incidents
      </button>

      <header className="rpt-header">
        <div>
          <h2>
            {CATEGORY_LABEL[incident.category]}
            {incident.report_number ? ` · ${incident.report_number}` : ''}
          </h2>
          <p className="rpt-sub">
            Reported {fmtDateTime(incident.created_at)} · review cycle{' '}
            {incident.review_cycle}
          </p>
        </div>
        <div className="rpt-header-actions">
          <StatusBadge status={incident.status} />
          {canReview &&
            NEXT_STATUSES[incident.status].map((next) => (
              <Button
                key={next}
                variant={next === 'rejected' ? 'danger' : 'primary'}
                onClick={() => onTransition(next)}
                disabled={transitionIncident.isPending}
              >
                {next === 'submitted'
                  ? 'Submit'
                  : next === 'under_review'
                    ? 'Start review'
                    : next === 'approved'
                      ? 'Approve'
                      : 'Reject'}
              </Button>
            ))}
          {canWrite && (
            <Button
              variant="danger"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteIncident.isPending}
            >
              Delete
            </Button>
          )}
        </div>
      </header>

      <div className="rpt-detail-grid">
        {/* ---- Main column: editor ---- */}
        <div className="rpt-card">
          <h3 className="rpt-card-title">Incident details</h3>
          <form className="rpt-form" onSubmit={onSaveEdit}>
            <FormRow>
              <FormField label="Category">
                <select
                  className="form-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as IncidentCategory)}
                  disabled={!canWrite || updateIncident.isPending}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Severity">
                <select
                  className="form-input"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
                  disabled={!canWrite || updateIncident.isPending}
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY_LABEL[s]}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormRow>
            <FormRow>
              <FormField label="Occurred at">
                <DateInput
                  value={occurredAt}
                  onChange={setOccurredAt}
                  withTime
                  disabled={!canWrite || updateIncident.isPending}
                />
              </FormField>
              <FormField label="Report number">
                <Input
                  type="text"
                  value={reportNumber}
                  onChange={(e) => setReportNumber(e.target.value)}
                  maxLength={120}
                  disabled={!canWrite || updateIncident.isPending}
                />
              </FormField>
            </FormRow>
            <FormField label="Narrative">
              <Textarea
                className="rpt-textarea"
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                rows={6}
                maxLength={50_000}
                disabled={!canWrite || updateIncident.isPending}
              />
            </FormField>
            <label className="rpt-checkbox">
              <input
                type="checkbox"
                checked={policeNotified}
                onChange={(e) => setPoliceNotified(e.target.checked)}
                disabled={!canWrite || updateIncident.isPending}
              />
              <span>Police / authorities notified</span>
            </label>
            {editError && (
              <p className="rpt-error" role="alert">
                {editError}
              </p>
            )}
            {canWrite && (
              <div className="rpt-form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={updateIncident.isPending}
                >
                  {updateIncident.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* ---- Side column: parties + timeline ---- */}
        <div className="rpt-detail-side">
          <div className="rpt-card">
            <div className="rpt-section-head">
              <h3>Parties</h3>
              {canWrite && (
                <button
                  type="button"
                  className="rpt-link"
                  onClick={() => setShowParty(true)}
                >
                  + Add
                </button>
              )}
            </div>
            {partiesQuery.isPending ? (
              <p className="rpt-status-line">Loading…</p>
            ) : parties.length === 0 ? (
              <p className="rpt-empty">No parties recorded.</p>
            ) : (
              parties.map((p) => (
                <div key={p.id} className="rpt-party">
                  <div>
                    <div className="rpt-party-type">{PARTY_TYPE_LABEL[p.party_type]}</div>
                    <div className="rpt-party-name">{p.name ?? '(unnamed)'}</div>
                    {p.contact && <div className="rpt-party-meta">{p.contact}</div>}
                    {p.details && <div className="rpt-party-meta">{p.details}</div>}
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      className="rpt-link rpt-link-danger"
                      onClick={() => {
                        deleteParty.mutate({ incidentId: id, partyId: p.id });
                      }}
                      disabled={deleteParty.isPending}
                      aria-label="Remove party"
                    >
                      remove
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="rpt-card">
            <div className="rpt-section-head">
              <h3>Event timeline</h3>
              {canWrite && (
                <button
                  type="button"
                  className="rpt-link"
                  onClick={() => setShowEvent(true)}
                >
                  + Add
                </button>
              )}
            </div>
            {eventsQuery.isPending ? (
              <p className="rpt-status-line">Loading…</p>
            ) : events.length === 0 ? (
              <p className="rpt-empty">No events logged yet.</p>
            ) : (
              <ul className="rpt-timeline">
                {events.map((ev) => (
                  <li key={ev.id} className="rpt-timeline-item">
                    <div className="rpt-timeline-type">{ev.event_type}</div>
                    <div className="rpt-timeline-time">{fmtDateTime(ev.event_at)}</div>
                    {ev.description && (
                      <div className="rpt-timeline-desc">{ev.description}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ---- Add-event modal ---- */}
      <Dialog
        title="Add timeline event"
        open={showEvent}
        busy={createEvent.isPending}
        onClose={() => !createEvent.isPending && setShowEvent(false)}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowEvent(false)}
              disabled={createEvent.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-event-form"
              variant="primary"
              disabled={createEvent.isPending}
            >
              {createEvent.isPending ? 'Adding…' : 'Add event'}
            </Button>
          </>
        }
      >
        <form id="add-event-form" className="rpt-form" onSubmit={onAddEvent}>
          <FormField label="Event type *">
            <Input
              type="text"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="e.g. arrival_on_scene, suspect_detained, ems_called"
              maxLength={120}
              disabled={createEvent.isPending}
            />
          </FormField>
          <FormField label="Occurred at">
            <DateInput
              value={eventAt}
              onChange={setEventAt}
              withTime
              disabled={createEvent.isPending}
            />
          </FormField>
          <FormField label="Description">
            <Textarea
              className="rpt-textarea"
              value={eventDesc}
              onChange={(e) => setEventDesc(e.target.value)}
              maxLength={50_000}
              disabled={createEvent.isPending}
            />
          </FormField>
          {eventError && (
            <p className="rpt-error" role="alert">
              {eventError}
            </p>
          )}
        </form>
      </Dialog>

      {/* ---- Add-party modal ---- */}
      <Dialog
        title="Add party"
        open={showParty}
        busy={createParty.isPending}
        onClose={() => !createParty.isPending && setShowParty(false)}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowParty(false)}
              disabled={createParty.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-party-form"
              variant="primary"
              disabled={createParty.isPending}
            >
              {createParty.isPending ? 'Adding…' : 'Add party'}
            </Button>
          </>
        }
      >
        <form id="add-party-form" className="rpt-form" onSubmit={onAddParty}>
          <FormField label="Type">
            <select
              className="form-input"
              value={partyType}
              onChange={(e) => setPartyType(e.target.value as IncidentPartyType)}
              disabled={createParty.isPending}
            >
              {PARTY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PARTY_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Name">
            <Input
              type="text"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              maxLength={200}
              disabled={createParty.isPending}
            />
          </FormField>
          <FormField label="Contact">
            <Input
              type="text"
              value={partyContact}
              onChange={(e) => setPartyContact(e.target.value)}
              placeholder="Phone, plate, badge #, …"
              maxLength={200}
              disabled={createParty.isPending}
            />
          </FormField>
          <FormField label="Details">
            <Textarea
              className="rpt-textarea"
              value={partyDetails}
              onChange={(e) => setPartyDetails(e.target.value)}
              maxLength={10_000}
              disabled={createParty.isPending}
            />
          </FormField>
          {partyError && (
            <p className="rpt-error" role="alert">
              {partyError}
            </p>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete incident"
        message="This soft-deletes the incident and removes it from the active list. Continue?"
        confirmLabel="Delete"
        danger
        busy={deleteIncident.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </section>
  );
}
